/**
 * Distribution curve utility functions for exit strategy planning
 */

// Exit strategy constraints
export const MIN_TARGET_MULTIPLE = 1.1;
export const MAX_TARGET_MULTIPLE = 20;
export const MIN_TAKE_INITIALS_MULTIPLE = 1.1;

// Distribution curve types (values match GraphQL DistributionCurve enum)
export const DISTRIBUTION_CURVES = {
  BELL: 'BELL',
  FRONT_LOADED: 'FRONT_LOADED',
  BACK_LOADED: 'BACK_LOADED',
  VALLEY: 'VALLEY'
};

/**
 * Applies a bell curve distribution
 * @param {number} position - Position from 0 to 1 (0 = start, 1 = end)
 * @param {number} focus - Focus point from 0 to 100 (0 = start, 50 = middle, 100 = end)
 * @param {number} intensity - Intensity from 0 to 100 (0 = flat, 100 = maximum curve)
 * @returns {number} - Weight value
 */
export const applyBellCurve = (position, focus, intensity) => {
  // Bell curve formula: e^(-(x-mean)²/(2*variance))
  const mean = focus / 100; // Normalize to 0-1
  const variance = 0.5 - (0.4 * (intensity / 100)); // Higher intensity = narrower bell curve
  
  // Center position around the focus point
  const x = position - mean;
  
  // Apply the gaussian function
  return Math.exp(-(x * x) / (2 * variance));
};

/**
 * Applies a front-loaded distribution (higher at start, lower at end)
 * @param {number} position - Position from 0 to 1 (0 = start, 1 = end)
 * @param {number} intensity - Intensity from 0 to 100 (0 = flat, 100 = maximum curve)
 * @returns {number} - Weight value
 */
export const applyFrontLoadedCurve = (position, intensity) => {
  // Exponential decay: e^(-kx) where k controls the rate of decay
  const k = 2 * (intensity / 100);
  return Math.exp(-k * position);
};

/**
 * Applies a back-loaded distribution (lower at start, higher at end)
 * @param {number} position - Position from 0 to 1 (0 = start, 1 = end)
 * @param {number} intensity - Intensity from 0 to 100 (0 = flat, 100 = maximum curve)
 * @returns {number} - Weight value
 */
export const applyBackLoadedCurve = (position, intensity) => {
  // Exponential growth: e^(k(x-1)) where k controls the rate of growth
  const k = 2 * (intensity / 100);
  return Math.exp(k * (position - 1));
};

/**
 * Applies a valley distribution (lower in the middle, higher at edges)
 * @param {number} position - Position from 0 to 1 (0 = start, 1 = end)
 * @param {number} focus - Focus point from 0 to 100 (0 = start, 50 = middle, 100 = end)
 * @param {number} intensity - Intensity from 0 to 100 (0 = flat, 100 = maximum valley)
 * @returns {number} - Weight value
 */
export const applyValleyCurve = (position, focus, intensity) => {
  // Creates a valley with higher values at the edges (inverse of peak)
  const valleyPosition = focus / 100;
  
  // Calculate distance from valley center (0 to 1)
  const distance = Math.abs(position - valleyPosition);
  
  // Use a softer curve that doesn't zero out the valley
  const width = 0.8 - (0.5 * (intensity / 100)); // Range: 0.8 (subtle) to 0.3 (max)
  
  // Inverted gaussian - high at edges, low in middle, but maintains minimum
  const minWeight = 0.15 - (0.1 * (intensity / 100)); // Range: 0.15 to 0.05
  const valleyWeight = 1 - Math.exp(-(distance * distance) / (2 * width * width));
  
  return minWeight + (1 - minWeight) * valleyWeight;
};

/**
 * Calculates distribution percentages based on curve settings
 * @param {number} count - Number of levels
 * @param {number} totalPercentage - Total percentage to distribute
 * @param {string} curveType - Type of curve to apply
 * @param {number} focus - Focus point from 0 to 100
 * @param {number} intensity - Intensity from 0 to 100
 * @returns {Array<number>} - Array of percentages
 */
export const calculateDistributedPercentages = (count, totalPercentage, curveType, focus, intensity) => {
  // If only one level, return total percentage
  if (count <= 1) {
    return [totalPercentage];
  }
  
  // If no curve specified or intensity is 0, return equal distribution
  if (!curveType || intensity === 0) {
    return Array(count).fill(totalPercentage / count);
  }
  
  // Calculate positions and weights for each level
  const weights = [];
  
  for (let i = 0; i < count; i++) {
    // Convert index to a position between 0 and 1
    const position = count > 1 ? i / (count - 1) : 0.5;
    
    // Get weight based on selected curve
    let weight;
    
    switch (curveType) {
      case DISTRIBUTION_CURVES.BELL:
        weight = applyBellCurve(position, focus, intensity);
        break;
      case DISTRIBUTION_CURVES.FRONT_LOADED:
        weight = applyFrontLoadedCurve(position, intensity);
        break;
      case DISTRIBUTION_CURVES.BACK_LOADED:
        weight = applyBackLoadedCurve(position, intensity);
        break;
      case DISTRIBUTION_CURVES.VALLEY:
        weight = applyValleyCurve(position, focus, intensity);
        break;
      default:
        // Default to equal weights
        weight = 1;
    }
    
    weights.push(weight);
  }
  
  // Normalize weights to sum to totalPercentage
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  const percentages = weights.map(w => (w / totalWeight) * totalPercentage);
  
  return percentages;
}; 